import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';

// Componentes modulares
import UserManagementHeader from '../components/UserManagement/UserManagementHeader';
import UsersTable from '../components/UserManagement/UsersTable';
import CreateUserModal from '../components/UserManagement/CreateUserModal';
import QuotaManagementModal from '../components/UserManagement/QuotaManagementModal';
import PendingQuotaModal from '../components/UserManagement/PendingQuotaModal';
import UserDocumentTypesModal from '../components/UserManagement/UserDocumentTypesModal';

// Hooks personalizados
import { useUserManagement, useUserActions, useUserUtils } from '../hooks/useUserManagement';

// Estilos
import '../styles/ModularComponents.css';

const UserManagement = () => {
  const { user } = useAuth();
  
  // Estados para gestión de cuotas
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [selectedUserForQuota, setSelectedUserForQuota] = useState(null);

  // Estado y lógica para el modal de solicitudes pendientes
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [selectedUserPending, setSelectedUserPending] = useState(null);

  // Estado para el modal de tipos de documentos
  const [showDocumentTypesModal, setShowDocumentTypesModal] = useState(false);
  const [selectedUserForDocuments, setSelectedUserForDocuments] = useState(null);

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Por defecto mostrar solo usuarios activos
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'user', 'admin'

  // Hook principal de gestión de usuarios
  const userState = useUserManagement(user);
  
  // Hook de utilidades
  const { formatDate, getRoleBadge, getStatusBadge } = useUserUtils();
  
  // Hook de acciones
  const { toggleUserStatus, createUser } = useUserActions(
    userState.users,
    localStorage.getItem('token'),
    userState.fetchUsers,
    userState.setProcessingUserId,
    userState.setShowCreateModal,
    userState.setNewUser
  );

  // Función para filtrar usuarios
  const filteredUsers = userState.users.filter(userData => {
    // Filtrar por término de búsqueda
    const matchesSearch = searchTerm === '' || 
      userData.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userData.email.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtrar por estado
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && userData.is_active) ||
      (statusFilter === 'inactive' && !userData.is_active);

    // Filtrar por rol
    const matchesRole = roleFilter === 'all' ||
      userData.role === roleFilter;

    return matchesSearch && matchesStatus && matchesRole;
  });

  // Funciones de manejo de eventos
  const handleToggleUserStatus = (userId, currentStatus) => {
    toggleUserStatus(userId, currentStatus, user);
  };

  const handleCreateUser = (e) => {
    createUser(e, userState.newUser);
  };

  const handleManageQuota = (userData) => {
    setSelectedUserForQuota(userData);
    setShowQuotaModal(true);
  };

  const handleShowPendingModal = (userData) => {
    setPendingList(userData.pending_quota_request || []);
    setSelectedUserPending(userData);
    setShowPendingModal(true);
  };

  const handleQuotaUpdated = (userId, updatedQuota) => {
    // Actualizar la cuota del usuario en la lista local
    // Aquí normalmente actualizarías el estado, pero como es un hook personalizado
    // necesitarías agregarlo al hook o refrescar la lista
    userState.fetchUsers();
  };

  // Usar las funciones del hook para aprobar/rechazar cuota
  const handleApproveQuota = (paymentId) => {
    userState.handleApproveQuota(paymentId, setShowPendingModal);
  };

  const handleRejectQuota = (paymentId) => {
    userState.handleRejectQuota(paymentId, setShowPendingModal);
  };

  // Funciones para el modal de tipos de documentos
  const handleViewDocumentTypes = (user) => {
    setSelectedUserForDocuments(user);
    setShowDocumentTypesModal(true);
  };

  const handleCloseDocumentTypesModal = () => {
    setShowDocumentTypesModal(false);
    setSelectedUserForDocuments(null);
  };

  if (userState.loading) {
    return (
      <Layout>
        <div className="d-flex align-items-center justify-content-center" style={{minHeight: '400px'}}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-muted">Cargando usuarios...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      
      <UserManagementHeader 
        onCreateUser={() => userState.setShowCreateModal(true)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        totalUsers={userState.users.length}
        filteredUsers={filteredUsers.length}
      />

      <UsersTable
        users={filteredUsers}
        currentUser={user}
        processingUserId={userState.processingUserId}
        onToggleUserStatus={handleToggleUserStatus}
        onManageQuota={handleManageQuota}
        onShowPendingModal={handleShowPendingModal}
        onViewDocumentTypes={handleViewDocumentTypes}
        formatDate={formatDate}
        getRoleBadge={getRoleBadge}
        getStatusBadge={getStatusBadge}
      />

      <CreateUserModal
        show={userState.showCreateModal}
        newUser={userState.newUser}
        setNewUser={userState.setNewUser}
        onClose={() => userState.setShowCreateModal(false)}
        onSubmit={handleCreateUser}
      />

      <QuotaManagementModal
        show={showQuotaModal}
        selectedUser={selectedUserForQuota}
        onClose={() => {
          setShowQuotaModal(false);
          setSelectedUserForQuota(null);
        }}
        onQuotaUpdated={handleQuotaUpdated}
      />

      <PendingQuotaModal
        show={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        pendingList={pendingList}
        selectedUser={selectedUserPending}
        onApprove={handleApproveQuota}
        onReject={handleRejectQuota}
        formatDate={formatDate}
      />

      <UserDocumentTypesModal
        show={showDocumentTypesModal}
        onClose={handleCloseDocumentTypesModal}
        selectedUser={selectedUserForDocuments}
      />

      
    </Layout>
  );
};

export default UserManagement;
